import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "../firebase";
import {
  collection, onSnapshot, doc, updateDoc,
  addDoc, serverTimestamp, getDocs, query, where, setDoc,
} from "firebase/firestore";

const NUM_SLOTS = 9;

const theme = {
  light: {
    bg: "#f8fafc", bg2: "#f1f5f9", bg3: "#e9eef5", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
    green: "#16a34a", greenBg: "#f0fdf4", greenBorder: "#bbf7d0",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    orange: "#ea580c", orangeBg: "#fff7ed", orangeBorder: "#fed7aa",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg2: "#131f30", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueBorder: "#1d4ed8",
    green: "#4ade80", greenBg: "#052e16", greenBorder: "#166534",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    orange: "#fb923c", orangeBg: "#2d1a00", orangeBorder: "#92400e",
    cardBg: "#1e293b",
  },
};

function getSlotNumber(slot) {
  if (slot.docId && slot.docId.startsWith("slot_")) {
    const n = parseInt(slot.docId.replace("slot_", ""), 10);
    if (!isNaN(n)) return n;
  }
  return slot.id ?? "?";
}

/* ─── 3×3 grid ───────────────────────────────────────────────────────────── */
function PomodoroGrid({ slots, highlightDocId, t }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
      gap: 10, marginBottom: 24,
    }}>
      {Array.from({ length: NUM_SLOTS }, (_, i) => {
        const num      = i + 1;
        const slot     = slots.find(s => getSlotNumber(s) === num);
        const available = slot ? (slot.available !== false && !slot.userId) : true;
        const isHL     = slot?.docId === highlightDocId;

        return (
          <div key={num} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, position: "relative" }}>
            <div style={{ position: "relative", width: "100%" }}>
              <img
                src="/3.png"
                alt={`Pomodoro ${num}`}
                style={{
                  width: "100%", display: "block",
                  filter: !available
                    ? "grayscale(0.85) brightness(0.5)"
                    : isHL ? "drop-shadow(0 0 6px #2563eb)" : "none",
                  transition: "filter 0.3s",
                }}
                onError={e => { e.target.style.fontSize = "40px"; e.target.style.display = "flex"; }}
              />
              {isHL && (
                <div style={{
                  position: "absolute", inset: -2, borderRadius: 6,
                  border: "2px solid #2563eb",
                  animation: "pulse 1s infinite", pointerEvents: "none",
                }} />
              )}
              {!available && (
                <div style={{
                  position: "absolute", inset: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <div style={{
                    background: "rgba(220,38,38,0.85)", borderRadius: 20,
                    padding: "2px 7px", fontSize: 8, fontWeight: 700, color: "#fff",
                  }}>PRIS</div>
                </div>
              )}
            </div>
            {/* label */}
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.02em",
              color: slot === undefined ? t.text3 : available ? t.green : t.red,
            }}>
              Pomodoro-{num}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */
export default function PomodoroPage() {
  const [dark, setDark]                 = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [slots, setSlots]               = useState([]);
  const [step, setStep]                 = useState("idle");
  const [assignedSlot, setAssignedSlot] = useState(null);
  const [activeBorrow, setActiveBorrow] = useState(null);
  const [seeding, setSeeding]           = useState(false);
  const [seedDone, setSeedDone]         = useState(false);
  const logDocRef                       = useRef(null);

  const t = dark ? theme.dark : theme.light;

  const toggleTheme = () => setDark(d => {
    localStorage.setItem("ws-theme", !d ? "dark" : "light");
    return !d;
  });

  // Live listener
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "pomodoro_slots"), snap => {
      const data = snap.docs
        .map(d => ({ docId: d.id, ...d.data() }))
        .sort((a, b) => getSlotNumber(a) - getSlotNumber(b));
      setSlots(data);
    });
    return () => unsub();
  }, []);

  // Check active borrow on mount
  useEffect(() => {
    const check = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const q = query(
        collection(db, "pomodoro_logs"),
        where("userId", "==", user.uid),
        where("status", "==", "active"),
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        setActiveBorrow({ id: d.id, ...d.data() });
        logDocRef.current = doc(db, "pomodoro_logs", d.id);
      }
    };
    check();
  }, []);

  // Restore guide once slots load
  useEffect(() => {
    if (activeBorrow && slots.length > 0 && step === "idle") {
      const slot = slots.find(s => s.docId === activeBorrow.slotDocId);
      if (slot) { setAssignedSlot(slot); setStep("guide"); }
    }
  }, [activeBorrow, slots]);

  const user           = auth.currentUser;
  const availableSlots = slots.filter(s => s.available !== false && !s.userId);
  const availableCount = availableSlots.length;
  const allLoaded      = slots.length > 0;

  // Seed 9 slots
  const handleSeed = async () => {
    setSeeding(true);
    for (let i = 1; i <= NUM_SLOTS; i++) {
      await setDoc(doc(db, "pomodoro_slots", `slot_${i}`), {
        available: true, userId: null, takenAt: null,
      });
    }
    setSeeding(false);
    setSeedDone(true);
  };

  const handleRequest = async () => {
    if (activeBorrow) { setStep("already_borrowed"); return; }
    const slot = availableSlots[0];
    if (!slot) { setStep("none"); return; }
    try {
      await updateDoc(doc(db, "pomodoro_slots", slot.docId), {
        available: false, userId: user?.uid ?? "unknown", takenAt: serverTimestamp(),
      });
      const logRef = await addDoc(collection(db, "pomodoro_logs"), {
        slotDocId: slot.docId, slotNum: getSlotNumber(slot),
        userId: user?.uid ?? "unknown",
        takenAt: serverTimestamp(), returnedAt: null, status: "active",
      });
      logDocRef.current = logRef;
      setActiveBorrow({ id: logRef.id, slotDocId: slot.docId });
      setAssignedSlot(slot);
      setStep("guide");
    } catch (err) { console.error(err); }
  };

  const handleResumeBorrow = () => {
    const slot = slots.find(s => s.docId === activeBorrow?.slotDocId);
    if (slot) setAssignedSlot(slot);
    if (activeBorrow) logDocRef.current = doc(db, "pomodoro_logs", activeBorrow.id);
    setStep("guide");
  };

  const handleReturn = async () => {
    if (!assignedSlot) return;
    try {
      await updateDoc(doc(db, "pomodoro_slots", assignedSlot.docId), {
        available: true, userId: null, takenAt: null,
      });
      if (logDocRef.current) {
        await updateDoc(logDocRef.current, { returnedAt: serverTimestamp(), status: "returned" });
        logDocRef.current = null;
      }
      setActiveBorrow(null);
      setStep("done");
      setTimeout(() => { setStep("idle"); setAssignedSlot(null); }, 3000);
    } catch (err) { console.error(err); }
  };

  const slotNum = assignedSlot ? getSlotNumber(assignedSlot) : null;

  return (
    <div style={{ background: t.bg, minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pop { 0%{transform:scale(0.85);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>

      <div style={{ maxWidth: 400, margin: "0 auto", padding: "0 18px", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <img src="/logo.PNG" alt="WeStudy" style={{ height: 28, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
          <button onClick={toggleTheme} style={{ width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", background: t.bg3, border: `1px solid ${t.border}` }}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: t.blue, position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: dark ? "translateX(16px)" : "translateX(0)" }} />
          </button>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 60 }}>

          {/* ── IDLE ── */}
          {step === "idle" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>

              <PomodoroGrid slots={slots} highlightDocId={null} t={t} />

              <div style={{ fontSize: 22, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Minuteurs Pomodoro</div>

              {activeBorrow && (
                <div style={{ background: t.orangeBg, border: `1px solid ${t.orangeBorder}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.orange, marginBottom: 4 }}>⏱ Emprunt en cours</div>
                  <div style={{ fontSize: 12, color: t.text2 }}>Vous avez un minuteur non remis. Pensez à le rendre à l'accueil !</div>
                </div>
              )}

              <div style={{ fontSize: 14, color: t.text2, marginBottom: 20, lineHeight: 1.6 }}>
                {!allLoaded
                  ? "Chargement…"
                  : availableCount > 0
                    ? `${availableCount} minuteur${availableCount > 1 ? "s" : ""} disponible${availableCount > 1 ? "s" : ""}`
                    : "Aucun minuteur disponible"
                }
              </div>

              {activeBorrow ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button onClick={handleResumeBorrow} style={{ width: "100%", border: "none", borderRadius: 14, background: t.orange, color: "#fff", fontSize: 16, fontWeight: 700, padding: 16, cursor: "pointer" }}>
                    ⏱ Voir mon minuteur emprunté
                  </button>
                  <button disabled style={{ width: "100%", border: "none", borderRadius: 14, background: t.bg3, color: t.text3, fontSize: 14, fontWeight: 600, padding: 12, cursor: "not-allowed", opacity: 0.5 }}>
                    ⏱ Emprunter un minuteur
                  </button>
                </div>
              ) : (
                <button onClick={handleRequest} disabled={!allLoaded || availableCount === 0} style={{
                  width: "100%", border: "none", borderRadius: 14,
                  background: allLoaded && availableCount > 0 ? t.blue : t.bg3,
                  color: allLoaded && availableCount > 0 ? "#fff" : t.text3,
                  fontSize: 16, fontWeight: 700, padding: 16,
                  cursor: allLoaded && availableCount > 0 ? "pointer" : "not-allowed",
                  opacity: allLoaded && availableCount > 0 ? 1 : 0.6,
                }}>
                  ⏱ Emprunter un minuteur
                </button>
              )}

              <div style={{ fontSize: 11, color: t.text3, marginTop: 14, marginBottom: 24 }}>
                Récupération à l'accueil WeStudy
              </div>

             

            </div>
          )}

          {/* ── GUIDE ── */}
          {step === "guide" && assignedSlot && (
            <div style={{ animation: "fadeIn 0.35s ease", textAlign: "center" }}>

              <PomodoroGrid slots={slots} highlightDocId={assignedSlot.docId} t={t} />

              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: t.greenBg, border: `1px solid ${t.greenBorder}`, borderRadius: 20, padding: "5px 14px", marginBottom: 16 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.green }}>✅ Minuteur emprunté</span>
              </div>

              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 4 }}>
                Pomodoro-{slotNum}
              </div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 20 }}>
                Récupérez-le à l'accueil WeStudy
              </div>

              <div style={{ background: t.cardBg, border: `1.5px solid ${t.border}`, borderRadius: 16, padding: "16px", marginBottom: 14, textAlign: "left" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text1, marginBottom: 14 }}>⏱ Comment utiliser le minuteur ?</div>
                {[
                  ["1", "Tournez le cadran vers la droite pour régler la durée souhaitée (max 90 min)."],
                  ["2", "Appuyez sur le bouton central pour démarrer le décompte."],
                  ["3", "Une vibration se déclenche à la fin du temps imparti."],
                  ["4", "Remettez le minuteur à l'accueil une fois terminé."],
                ].map(([num, text]) => (
                  <div key={num} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: num !== "4" ? 12 : 0 }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, background: t.blueBg, border: `1.5px solid ${t.blueBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: t.blue }}>{num}</div>
                    <div style={{ fontSize: 12, color: t.text1, lineHeight: 1.5, paddingTop: 3 }}>{text}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: t.blueBg, border: `1px solid ${t.blueBorder}`, borderRadius: 12, padding: "10px 14px", marginBottom: 20, display: "flex", gap: 8, alignItems: "center", textAlign: "left" }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>ℹ️</span>
                <span style={{ fontSize: 11, color: t.blue, lineHeight: 1.5 }}>Remettez le minuteur à <strong>l'accueil WeStudy</strong> après utilisation.</span>
              </div>

              <button onClick={handleReturn} style={{ width: "100%", border: "none", borderRadius: 14, background: t.red, color: "#fff", fontSize: 15, fontWeight: 700, padding: 15, cursor: "pointer" }}>
                ⏱ Remettre le minuteur
              </button>
            </div>
          )}

          {/* ── DONE ── */}
          {step === "done" && (
            <div style={{ animation: "pop 0.4s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.greenBg, border: `2px solid ${t.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Merci !</div>
              <div style={{ fontSize: 13, color: t.text2 }}>Minuteur remis avec succès.</div>
            </div>
          )}

          {/* ── ALREADY BORROWED ── */}
          {step === "already_borrowed" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: t.orangeBg, border: `2px solid ${t.orange}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 20px" }}>⚠️</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: t.text1, marginBottom: 8 }}>Emprunt en cours</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32, lineHeight: 1.6 }}>Vous avez déjà un minuteur en cours.<br />Remettez-le avant d'en emprunter un autre.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Retour</button>
            </div>
          )}

          {/* ── NONE ── */}
          {step === "none" && (
            <div style={{ animation: "fadeIn 0.3s ease", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 8 }}>Aucun minuteur disponible</div>
              <div style={{ fontSize: 13, color: t.text2, marginBottom: 32 }}>Tous les minuteurs sont empruntés pour le moment.</div>
              <button onClick={() => setStep("idle")} style={{ border: "none", borderRadius: 12, background: t.bg3, color: t.text2, fontSize: 14, fontWeight: 600, padding: "12px 24px", cursor: "pointer" }}>← Réessayer</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}